import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BlockRenderElement, Section } from './types';
import { useEditorStore } from './editor-store';
import classNames from 'classnames';
import { useFeatureFlag } from './feature-flags-store';

import { useReactSpringScrollTo } from './utils/react-spring-scroll-to.hook';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { useUpdateEffect } from 'react-use';
// import { memoizeRenderBlockView } from './utils/memoize-render-block-view';
import { shallow } from 'zustand/shallow';
import { MemoizedAnimatedBoundaryViews } from './BoundaryViews';
import { getScrollYToActivateSection } from './helpers/get-active-section-id';
import { MemoizedAnimatedResizeBoundaryViews } from './resize-boundary-views';
import { MemoizedAnimatedBlocksViewInner } from './animated-blocks-view-inner';

const fixNumber = (number: number, precision: number) => parseFloat(number.toFixed(precision));

// const MemoizedResizeBoundaryView = React.memo(ResizeBoundaryView, (prevProps, nextProps) => {
//   const { boundary: prevBoundary, dragging: prevDragging, show: prevShow } = prevProps;
//   const { boundary: nextBoundary, dragging: nextDragging, show: nextShow } = nextProps;

//   if (prevShow !== nextShow) {
//     return false;
//   }

//   // there is an ongoing resizing || resizing has been stopped
//   if ((prevDragging && nextDragging) || (prevDragging && !nextDragging)) {
//     return false;
//   }

//   const fixedPrevBoundaryX = fixNumber(prevBoundary.x, 2);
//   const fixedPrevBoundaryY = fixNumber(prevBoundary.y, 2);

//   const fixedNextBoundaryX = fixNumber(nextBoundary.x, 2);
//   const fixedNextBoundaryY = fixNumber(nextBoundary.y, 2);

//   if (
//     Math.abs(fixedPrevBoundaryX - fixedNextBoundaryX) > RESIZE_RENDER_THRESHOLD ||
//     Math.abs(fixedPrevBoundaryY - fixedNextBoundaryY) > RESIZE_RENDER_THRESHOLD
//   ) {
//     return false;
//   }

//   return true;
// });

export const AnimatedBlocksView = () => {
  // const logPerf = createPerformanceLogger('AnimatedBlocksViewPerf');
  const debugEditorAreas = useFeatureFlag('debug_editor_areas');
  const innerAreaRect = useEditorStore((state) => state.innerAreaRect);
  const isDraggingSomethingOnEditor = useEditorStore((state) => state.isDraggingSomethingOnEditor);
  const editorContentElement = useEditorStore((state) => state.editorContentElement);
  const scrollToBlockId = useEditorStore((state) => state.scrollToBlockId);
  const scrollToSectionId = useEditorStore((state) => state.scrollToSectionId);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
  const setScrollToSectionId = useEditorStore((state) => state.setScrollToSectionId);
  const layout = useEditorStore((state) => state.layout, shallow);

  const { renderElements, sections, sectionLayoutInfos, innerActualWidth, innerActualHeight } = layout;

  useUpdateEffect(() => {
    if (scrollToBlockId && editorContentElement) {
      const renderEl = renderElements.find((re) => re.type === 'block' && re.block.id === scrollToBlockId) as
        | BlockRenderElement
        | undefined;
      if (!renderEl) {
        return;
      }
      const { rect, block } = renderEl;
      if (!rect || !block) {
        return;
      }
      const totalHeight = editorContentElement.getBoundingClientRect().height;
      let scrollY = 0;
      if (block.layout.spec.type === 'calculated-height') {
        // Maybe we don't have the height yet
        scrollY = rect.y - Math.max((totalHeight - 400) / 2, 16);
      } else {
        scrollY = rect.y - Math.max((totalHeight - rect.height) / 2, 16);
      }
      editorContentElement.scrollTo({
        top: scrollY,
        // top: 100,
        behavior: 'smooth',
      });
    }
  }, [scrollToBlockId]);

  const { scrollTo } = useReactSpringScrollTo();

  const innerAreaHeight = innerAreaRect?.height || null;

  const scrollToSection = useRefCallback(
    (sectionId: Section['id']) => {
      if (!innerAreaHeight) return;
      if (!editorContentElement) return;
      const scrollY = getScrollYToActivateSection({
        sections,
        sectionLayoutInfos,
        sectionId,
        innerAreaHeight,
      });
      if (scrollY === undefined) {
        return;
      }
      scrollTo(editorContentElement, scrollY, () => {
        setScrollToSectionId(null);
      });
    },
    [sections, sectionLayoutInfos, innerAreaHeight, editorContentElement, scrollTo]
  );

  useUpdateEffect(() => {
    if (scrollToSectionId) {
      scrollToSection(scrollToSectionId);
    }
  }, [scrollToSectionId, scrollToSection]);

  // const scrollToInitialSection = useRefCallback(() => {
  //   if (!innerAreaHeight) return;
  //   if (!editorContentElement) return;
  //   if (!initialSectionId) return;
  //   const scrollY = getScrollYToActivateSection({
  //     sections,
  //     sectionLayoutInfos,
  //     sectionId: initialSectionId,
  //     innerAreaHeight,
  //   });
  //   if (scrollY === undefined) {
  //     return;
  //   }
  //   editorContentElement.scrollTo({
  //     top: scrollY,
  //   });
  //   setTimeout(() => {
  //     clearScrollToInitialSection();
  //   }, 100);
  // }, [sections, sectionLayoutInfos, innerAreaHeight, editorContentElement, initialSectionId, scrollTo]);

  // useEffect(() => {
  //   if (initialSectionId) {
  //     scrollToInitialSection();
  //   }
  // }, [sections, sectionLayoutInfos, innerAreaHeight, editorContentElement, initialSectionId, scrollToInitialSection]);

  const containerRef = useRef<HTMLDivElement>(null);

  const onCanvasClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target === containerRef.current) {
      clearSelectedBlock();
    }
  };

  return (
    <div
      style={{ width: innerActualWidth || 0, height: innerActualHeight || 0 }}
      className={classNames('relative', {
        'bg-blue-400/20': debugEditorAreas,
        'selection:bg-none selection:text-none': isDraggingSomethingOnEditor,
      })}
      ref={containerRef}
      onClick={onCanvasClick}
    >
      {innerActualWidth ? (
        <>
          <MemoizedAnimatedBlocksViewInner />
          <MemoizedAnimatedBoundaryViews />
          <MemoizedAnimatedResizeBoundaryViews />
          {/* {debugBoundaries ? <BoundariesDebugView boundaries={boundaries} /> : null} */}
        </>
      ) : null}
    </div>
  );
};
// AnimatedBlocksView.whyDidYouRender = true;
